import React, {useMemo, useRef, useState, useEffect} from "react";
import {HeaderCard} from "../Base/HeaderCard";
import {NavigationHeader} from "../Navigation/NavigationHeader";
import {useTranslation} from "react-i18next";
import {
    Box,
    Alert,
    Card,
    CardContent,
    CardHeader,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Typography
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import elearningClasses from '../../styling/components/DemoElearningOverview.module.css'
import Lottie from "lottie-react";
import successAnimation from "../../styling/preview/Lottie/Success_Animation.json";

interface Props {
    courseUrl?: string | null;
    courseName?: string;
}

export const DemoElearningOverview: React.FC<Props> = ({courseUrl, courseName}) => {
    const {t} = useTranslation()
    const [showCourse, setShowCourse] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    const courseOrigin = useMemo(() => {
        if (!courseUrl) {
            return null;
        }
        try {
            return new URL(courseUrl).origin;
        } catch (error) {
            console.warn("Invalid course URL provided to DemoElearningOverview", error);
            return null;
        }
    }, [courseUrl]);

    useEffect(() => {
        if (!courseOrigin) {
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.data !== "kurs_beenden") {
                return;
            }

            if (event.origin !== courseOrigin) {
                console.warn(`Ignoring kurs_beenden message from unexpected origin: ${event.origin}`);
                return;
            }

            if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
                return;
            }

            setShowCourse(false);
            setShowSuccessDialog(true);
        };

        window.addEventListener("message", handleMessage);
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [courseOrigin]);

    const navigation = [
        {url: "", label: t("elearning.title")}
    ]

    return (
        <>
            <NavigationHeader path={navigation}/>
            <HeaderCard 
                headline={<>{t('elearning.welcomeHeader')}</>}
                body={<>{t('elearning.welcomeText')}</>}
            />
            
            <Card className={elearningClasses.card}>
                {courseName && (
                    <CardHeader 
                        title={courseName}
                        className={elearningClasses.cardHeader}
                    />
                )}
                <CardContent className={elearningClasses.cardContent}>
                    {courseUrl ? (
                        showCourse ? (
                            <div className={elearningClasses.iframeContainer}>
                                <iframe
                                    src={courseUrl}
                                    title="Demo E-learning Module"
                                    className={elearningClasses.iframe}
                                    allow="fullscreen"
                                    ref={iframeRef}
                                />
                            </div>
                        ) : (
                            <div className={elearningClasses.startContainer}>
                                <Button
                                    variant="contained"
                                    className={elearningClasses.playButton}
                                    onClick={() => setShowCourse(true)}
                                >
                                    <PlayArrowIcon fontSize="large" />
                                </Button>
                            </div>
                        )
                    ) : (
                        <Box className={elearningClasses.alertContainer}>
                            <Alert severity="info">{t('elearning.noCourseUrlConfigured')}</Alert>
                        </Box>
                    )}
                </CardContent>
            </Card>
            <Dialog
                open={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogContent dividers>
                    <Box className={elearningClasses.successDialogContent}>
                        <Typography variant="h6" className={elearningClasses.successTitle}>
                            {t('elearning.successTitle')}
                        </Typography>
                        <Lottie
                            animationData={successAnimation}
                            loop
                            className={elearningClasses.successAnimation}
                        />
                        <Typography variant="body1" className={elearningClasses.successBody}>
                        {t('elearning.successBody')}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="look2xBlue" onClick={() => setShowSuccessDialog(false)}>
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
